import { Typography } from "@cpj-ai/panel-npm-package";
import { useOrganizationStore } from "@store/useOrganizationStore";
import React, { useMemo } from "react";
import { TJobPositionInterviews } from ".";
import InterviewCard from "./InterviewCard";
import { InterviewType } from "./types";

interface InterviewTimelineProps {
  interviews: TJobPositionInterviews;
  assignmentId?: string;
  refetch: () => void;
}

const adaptInterview = (interview: NonNullable<TJobPositionInterviews>[number]): InterviewType => {
  return {
    id: interview?.event?.meetUrl?.split("/").pop() ?? "meeting-id",
    interviewDate: interview?.event?.datetime?.[0] ?? new Date().toISOString(),
    canceledBy: null,
    type: "Interview",
    location: "Online",
    status: interview?.event?.status ?? "SCHEDULED",
  };
};

const InterviewTimeline: React.FC<InterviewTimelineProps> = ({ interviews, assignmentId, refetch }) => {
  const { selectedOrganization } = useOrganizationStore();
  const { upcomingInterviews, pastInterviews, nextInterview } = useMemo(() => {
    const now = new Date().getTime();

    const sorted = [...(interviews ?? [])].sort((a, b) => {
      const aTime = new Date(a?.event?.datetime?.[0] ?? "").getTime();
      const bTime = new Date(b?.event?.datetime?.[0] ?? "").getTime();

      const aIsUpcoming = aTime > now;
      const bIsUpcoming = bTime > now;

      if (aIsUpcoming && !bIsUpcoming) return -1;
      if (!aIsUpcoming && bIsUpcoming) return 1;

      return aIsUpcoming ? aTime - bTime : bTime - aTime;
    });

    const upcoming = sorted.filter(
      (interview) =>
        interview?.event?.status !== "CANCELED" &&
        interview?.event?.status !== "COMPLETED" &&
        new Date(interview?.event?.datetime?.[0] ?? "").getTime() > now,
    );

    const past = sorted.filter(
      (interview) =>
        interview?.event?.status === "CANCELED" ||
        interview?.event?.status === "COMPLETED" ||
        new Date(interview?.event?.datetime?.[0] ?? "").getTime() <= now,
    );

    const sortedUpcoming = [...upcoming].sort((a, b) => {
      const aTime = new Date(a?.event?.datetime?.[0] ?? "").getTime();
      const bTime = new Date(b?.event?.datetime?.[0] ?? "").getTime();
      return aTime - bTime;
    });

    const sortedPast = [...past].sort((a, b) => {
      const aTime = new Date(a?.event?.datetime?.[0] ?? "").getTime();
      const bTime = new Date(b?.event?.datetime?.[0] ?? "").getTime();
      return bTime - aTime;
    });

    const next = sortedUpcoming.length > 0 ? sortedUpcoming[0] : null;
    const remainingUpcoming = next ? sortedUpcoming.slice(1) : sortedUpcoming;

    return {
      upcomingInterviews: remainingUpcoming,
      pastInterviews: sortedPast,
      nextInterview: next,
    };
  }, [interviews]);

  return (
    <div className="space-y-6">
      {(nextInterview || upcomingInterviews.length > 0) && (
        <div>
          <Typography className="font-semibold mb-3">Upcoming Interviews</Typography>
          <div className="space-y-4">
            {nextInterview && (
              <InterviewCard
                interview={adaptInterview(nextInterview)}
                isUpcoming={true}
                isNext={true}
                companyName={selectedOrganization?.name}
                interviewUrl={nextInterview.event?.meetUrl}
                assignmentId={assignmentId}
                refetch={refetch}
              />
            )}

            {upcomingInterviews?.map((interview, index) => (
              <InterviewCard
                key={index}
                interview={adaptInterview(interview)}
                isUpcoming={true}
                isNext={false}
                companyName={selectedOrganization?.name}
                interviewUrl={interview?.event?.meetUrl}
                assignmentId={assignmentId}
                refetch={refetch}
              />
            ))}
          </div>
        </div>
      )}

      {pastInterviews.length > 0 && (
        <div>
          <Typography className="font-semibold mb-3 mt-6">Past Interviews</Typography>
          <div className="space-y-4 opacity-80">
            {pastInterviews.map((interview, index) => (
              <InterviewCard
                key={index}
                interview={adaptInterview(interview)}
                isUpcoming={false}
                isNext={false}
                companyName={selectedOrganization?.name}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewTimeline;
